import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import type { LocationRole, AssignLocationUserFormData } from '@/types/location-users'
import { LOCATION_ROLES, LOCATION_ROLE_LABELS, LOCATION_ROLE_DESCRIPTIONS } from '@/types/location-users'

interface AssignUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  availableUsers: Array<{ user_id: string; email: string; display_name: string | null }>
  onAssign: (data: AssignLocationUserFormData) => Promise<void>
  isLoading?: boolean
}

export function AssignUserDialog({
  open,
  onOpenChange,
  availableUsers,
  onAssign,
  isLoading = false,
}: AssignUserDialogProps) {
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [selectedRole, setSelectedRole] = useState<LocationRole>('viewer')
  const [isSubmitting, setIsSubmitting] = useState(false)

const canSubmit = selectedUserId && !isSubmitting && !isLoading

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit || !selectedUserId) return

    setIsSubmitting(true)
    try {
      await onAssign({
        user_id: selectedUserId,
        location_role: selectedRole,
      })
      setSelectedUserId('')
      setSelectedRole('viewer')
      onOpenChange(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign User to Location</DialogTitle>
          <DialogDescription>
            Select a team member and assign them a role at this location
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="user-select">Team Member</Label>
            {availableUsers.length === 0 ? (
              <div className="flex items-center justify-center h-10 rounded-md border border-input bg-background text-sm text-muted-foreground">
                All team members already assigned
              </div>
            ) : (
              <Select value={selectedUserId} onValueChange={setSelectedUserId} disabled={isSubmitting || isLoading}>
                <SelectTrigger id="user-select">
                  <SelectValue placeholder="Select a team member..." />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map((user) => (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      <div className="flex flex-col gap-0.5">
                        <span>{user.display_name || 'Unnamed'}</span>
                        <span className="text-xs text-muted-foreground">{user.email}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="role-select">Role</Label>
            <Select
              value={selectedRole}
              onValueChange={(value) => setSelectedRole(value as LocationRole)}
              disabled={isSubmitting || isLoading}
            >
              <SelectTrigger id="role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LOCATION_ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    {LOCATION_ROLE_LABELS[role]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {LOCATION_ROLE_DESCRIPTIONS[selectedRole]}
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting || isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
            >
              {isSubmitting ? 'Assigning...' : 'Assign User'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
